import {
  ActionMenu,
  ActionMenuItem,
  ActionMenuItemSpacer,
} from '@frontapp/ui-kit';
import { addSeconds, formatDistanceToNowStrict } from 'date-fns';
import { useNotifications } from 'reapop';
import Ellipsis from './Ellipsis';
import CopyActionMenuItem from './menuItems/CopyActionMenuItem';
import GotoActionMenuItem from './menuItems/GotoActionMenuItem';
import { type Organization, levelEmoji } from './types';
import { useInternalClient } from './utils/useInternalClient';

type Props = {
  organization: Organization;
};

function OrganizationInfo({ organization }: Props) {
  const client = useInternalClient();
  const { notify } = useNotifications();

  async function handleResetInvitationEmailsLimit() {
    try {
      await client.resetOrganizationInvitationEmailsBudget(organization.id);
      notify(
        `Invitation emails limit reset for ${organization.name}!`,
        'success',
      );
    } catch (e) {
      notify('Something went wrong...', 'error');
    }
  }

  return (
    <tr>
      <td>
        <Ellipsis
          maxWidth={300}
          content={`${levelEmoji[organization.plan.support_level]} ${
            organization.name
          }${organization.is_agency_partner ? ' (Partner 🤝)' : ''}${
            organization.appointed_organizations.length > 0
              ? ' (with mandate)'
              : ''
          }${
            organization.referrer_organization
              ? ` (referred by ${organization.referrer_organization.name})`
              : ''
          }`}
        />
      </td>
      <td>{organization.sites.length}</td>
      <td>
        <ActionMenu width={250}>
          <GotoActionMenuItem
            title="Enter organization as owner"
            url={organization.owner.dashboard_enter_url}
          />
          <GotoActionMenuItem
            title="Enter organization as account"
            url={organization.current_account.dashboard_enter_url}
          />

          <ActionMenuItemSpacer />

          <CopyActionMenuItem
            title="Organization ID"
            isId={true}
            value={organization.id}
          />

          <CopyActionMenuItem
            title="Organization name"
            value={organization.name}
          />

          {organization.appointed_organizations.map((agency) => (
            <CopyActionMenuItem
              key={agency.id}
              title="Partner 🤝"
              value={agency.name}
            />
          ))}

          {organization.referrer_organization && (
            <CopyActionMenuItem
              title="Referred by"
              value={organization.referrer_organization.name}
            />
          )}

          <ActionMenuItemSpacer />

          <CopyActionMenuItem
            title="Plan ID"
            isId={true}
            value={organization.plan.id}
          />
          <CopyActionMenuItem
            title="Plan name"
            value={organization.plan.name}
          />

          {organization.per_owner_pricing_adjuster_ttl && (
            <CopyActionMenuItem
              title="Plan adjusting in"
              value={formatDistanceToNowStrict(
                addSeconds(
                  new Date(),
                  organization.per_owner_pricing_adjuster_ttl,
                ),
              )}
            />
          )}

          <GotoActionMenuItem
            title="Edit in Sales Tools"
            url={`https://sales-tools.datocms.com/per-owner-pricing-plans/${organization.plan.id}`}
          />

          {organization.chargebee_customer_id && (
            <GotoActionMenuItem
              title="Open in Chargebee"
              url={`https://datocms.chargebee.com/customers?view_code=all&Customers.search=${organization.chargebee_customer_id}`}
            />
          )}

          <ActionMenuItemSpacer />

          <ActionMenuItem
            iconName="CheckmarkCircle"
            onClick={handleResetInvitationEmailsLimit}
          >
            Reset invitation emails limit
          </ActionMenuItem>
        </ActionMenu>
      </td>
    </tr>
  );
}

export default OrganizationInfo;
